import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Search, Share2, Send, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { orderLabel } from '../utils/itemName';

const money = (value) => `Rs ${(value ?? 0).toFixed(2)}`;

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

/**
 * Prints a bill for a token.
 *
 * The flow follows what happens at the counter: someone asks for a bill, the
 * cashier types the token number off their slip, checks the items that come
 * back are the right ones, then puts a name to it.
 *
 * A token can be billed whether or not it has been paid - a bill handed over
 * before payment and a receipt handed over after are the same piece of paper,
 * so the only difference is the PAID stamp.
 *
 * Opened with a `tokenNumber` the typing is skipped: the cashier has already
 * got that bill on screen and tapped it, so its lines are fetched straight
 * away. The lookup box stays put either way, as the customer at the counter is
 * not always the token that happens to be open.
 */
function BillModal({ onClose, tokenNumber = null }) {
  const { t, language } = useLanguage();
  const [token, setToken] = useState(tokenNumber ? String(tokenNumber) : '');
  const [bill, setBill] = useState(null);
  const [name, setName] = useState('');
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The public copy: created on demand, then sent or scanned.
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [copied, setCopied] = useState(false);

  /*
    Marks the document while a bill is on screen, so the print rules only strip
    the app away when there is actually a paper copy to print. Without it the
    same rules would blank the public bill page a customer prints from a shared
    link.
  */
  useEffect(() => {
    if (!bill) return undefined;
    document.body.classList.add('printing-bill');
    return () => document.body.classList.remove('printing-bill');
  }, [bill]);

  const lookupToken = useCallback(async (rawToken) => {
    const wanted = parseInt(rawToken, 10);

    if (!Number.isInteger(wanted) || wanted < 1) {
      setError(t('Enter a valid token number'));
      return;
    }

    setError(null);
    setLooking(true);

    try {
      const response = await api.get(`/api/orders/bill/${wanted}`);
      setBill(response.data);
      // A token billed before keeps its name, so a reprint needs no retyping
      setName(response.data.billedTo || '');
    } catch (lookupError) {
      setBill(null);
      setError(lookupError.response?.data?.message || 'Could not find that token.');
    } finally {
      setLooking(false);
    }
  }, [t]);

  const handleLookup = () => lookupToken(token);

  // Opened from a token already on screen - fetch it rather than making the
  // cashier type back a number they just tapped
  useEffect(() => {
    if (tokenNumber) {
      lookupToken(tokenNumber);
    }
  }, [tokenNumber, lookupToken]);

  const handlePrint = async () => {
    if (!bill) return;

    if (!name.trim()) {
      setError(t('Enter a name for the bill'));
      return;
    }

    setError(null);
    setSaving(true);

    try {
      // Saved before printing so the name is on the record even if the print
      // dialog is cancelled, and so a reprint comes back identical
      const response = await api.put(`/api/orders/bill/${bill.tokenNumber}`, {
        billedTo: name.trim()
      });
      setBill(response.data);
      window.print();
    } catch (saveError) {
      setError(saveError.response?.data?.message || 'Could not save the bill.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Create (or fetch) the public link for this bill and open the share panel.
   *
   * The server reuses an existing token, so sending the link twice - or scanning
   * the QR after already sending the message - lands on the same bill rather
   * than leaving several live links to one receipt.
   */
  const handleShare = async () => {
    if (!bill) return;

    setError(null);
    setSharing(true);

    try {
      const response = await api.post(`/api/orders/bill/${bill.tokenNumber}/share`);
      // Built from the browser's own origin so the link works wherever the app
      // is served from, rather than a hostname baked in at build time.
      setShareUrl(`${window.location.origin}/bill/${response.data.shareToken}`);
    } catch (shareError) {
      setError(shareError.response?.data?.message || 'Could not create a share link.');
    } finally {
      setSharing(false);
    }
  };

  const billSummary = () => {
    if (!bill) return '';
    const lines = bill.items
      .map((line) => `• ${orderLabel(line, language)}${line.quantity > 1 ? ` x${line.quantity}` : ''} - ${money(line.amount)}`)
      .join('\n');
    return `*${bill.tenantName || 'Bill'}* - Token #${bill.tokenNumber}\n${lines}\n*Total: ${money(bill.total)}*\n\n${shareUrl}`;
  };

  const handleWhatsApp = () => {
    // Without a number WhatsApp opens its own contact picker, which is what a
    // walk-in customer needs - there is nobody on file to send it to.
    const digits = (whatsappNumber || '').replace(/\D/g, '');
    const base = digits ? `https://wa.me/${digits}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(billSummary())}`, '_blank', 'noopener');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('Could not copy - select the link and copy it by hand.'));
    }
  };

  const reset = () => {
    setBill(null);
    setName('');
    setToken('');
    setError(null);
    setShareUrl('');
    setWhatsappNumber('');
    setCopied(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0 no-print">
          <h2 className="text-xl font-semibold text-gray-900">{t('Print a Bill')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Step 1 - which token */}
          <div className="space-y-2 no-print">
            <label className="block text-sm font-medium text-gray-700">{t('Token Number')}</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLookup();
                }}
                disabled={looking}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                placeholder="e.g. 3"
                autoFocus={!tokenNumber}
              />
              <button
                onClick={handleLookup}
                disabled={looking}
                className="px-4 py-2 bg-cafe-600 text-white rounded-md hover:bg-cafe-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                {looking ? 'Finding...' : 'Find'}
              </button>
            </div>
            {bill && (
              <button
                onClick={reset}
                className="text-sm text-cafe-600 hover:text-cafe-700 font-medium"
              >
                Look up a different token
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 no-print">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Step 2 - who it is for */}
          {bill && (
            <>
              <div className="space-y-2 no-print">
                <label className="block text-sm font-medium text-gray-700">{t('Bill To')}</label>
                {/* Opened on a known token the lookup box is skipped, so this is
                    the first thing left to fill in */}
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                  placeholder={t('Customer or company name')}
                  autoFocus={Boolean(tokenNumber)}
                />
              </div>

              {/* On-screen preview. The paper copy is a separate render in a
                  portal below - see the comment there for why. */}
              <div className="border border-gray-200 rounded-lg p-5 bg-white">
                <BillPaper bill={bill} billedTo={name} />
              </div>
            </>
          )}
        </div>

        {/* Share panel. Only reachable once a bill is on screen, so there is
            always something to share. */}
        {bill && shareUrl && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-cafe-50 no-print space-y-3">
            <div className="flex items-start gap-4">
              {/* Rendered as SVG so it stays crisp when a customer leans in with
                  a phone camera, and prints cleanly if the bill is printed. */}
              <div className="bg-white p-2 rounded-lg border border-gray-200 flex-shrink-0">
                <QRCodeSVG value={shareUrl} size={104} level="M" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{t('Scan or send')}</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {t('The customer can open this without an account.')}
                </p>
                <button
                  onClick={handleCopy}
                  className="mt-2 text-xs font-medium text-cafe-700 inline-flex items-center gap-1.5 hover:underline"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Link copied' : 'Copy link'}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="tel"
                inputMode="numeric"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder={t('WhatsApp number with country code (optional)')}
                aria-label={t('WhatsApp number')}
                className="flex-1 min-w-0 px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
              />
              <button
                onClick={handleWhatsApp}
                className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold whitespace-nowrap"
              >
                <Send className="h-4 w-4" />
                {t('WhatsApp')}
              </button>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50 no-print">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onClose}
              className="flex-1 min-w-[100px] px-4 py-2 min-h-[44px] text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleShare}
              disabled={!bill || sharing}
              className="flex-1 min-w-[100px] px-4 py-2 min-h-[44px] bg-white text-cafe-700 border border-cafe-300 rounded-md hover:bg-cafe-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Share2 className="h-4 w-4" />
              {sharing ? 'Creating...' : shareUrl ? 'Refresh link' : 'Share'}
            </button>
            <button
              onClick={handlePrint}
              disabled={!bill || saving || !name.trim()}
              className="flex-1 min-w-[100px] px-4 py-2 min-h-[44px] bg-cafe-600 text-white rounded-md hover:bg-cafe-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Printer className="h-4 w-4" />
              {saving ? 'Saving...' : 'Print'}
            </button>
          </div>
        </div>
      </div>

      {/*
        The paper copy, rendered straight into <body> rather than left inside
        the modal.

        Printing it in place produced two identical pages. The modal wrapper is
        `position: fixed`, and a fixed element is repeated on every printed page
        by Chrome and Safari; the old rule hid the rest of the app with
        `visibility: hidden`, which leaves every hidden node occupying its full
        height, so the document was tall enough to need a second page and the
        bill was stamped onto both. The same containing block was also what
        pushed the Amount column past the right edge.

        Out here it has no positioned ancestor and no inherited padding, so it
        simply flows inside the @page margins.
      */}
      {bill &&
        createPortal(
          <div className="bill-print-portal">
            <BillPaper bill={bill} billedTo={name} />
          </div>,
          document.body
        )}
    </div>
  );
}

/**
 * The bill as it appears on paper, and as previewed on screen.
 *
 * One component for both so the preview cannot drift from what actually prints.
 */
function BillPaper({ bill, billedTo }) {
  const { t, language } = useLanguage();
  return (
    <div className="bill-paper">
      <div className="text-center border-b border-gray-300 pb-3 mb-3">
        {bill.tenantName && <h3 className="text-lg font-bold text-gray-900">{bill.tenantName}</h3>}
        <p className="text-sm text-gray-600 mt-1">Bill · Token #{bill.tokenNumber}</p>
        <p className="text-xs text-gray-500">{formatDateTime(bill.issuedAt)}</p>
      </div>

      <div className="text-sm space-y-1 mb-3">
        <div className="flex justify-between gap-4">
          <span className="text-gray-600">{t('Bill to')}</span>
          <span className="font-medium text-gray-900 text-right">
            {(billedTo || '').trim() || '—'}
          </span>
        </div>
        {bill.completedAt && (
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">{t('Paid at')}</span>
            <span className="text-gray-900">{formatDateTime(bill.completedAt)}</span>
          </div>
        )}
      </div>

      <table className="w-full text-sm border-t border-gray-300">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="font-medium py-1">{t('Item')}</th>
            <th className="font-medium py-1 text-center w-12">{t('Qty')}</th>
            <th className="font-medium py-1 text-right w-20">{t('Rate')}</th>
            <th className="font-medium py-1 text-right w-24">{t('Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {bill.items.map((line, index) => (
            <tr key={`${bill.tokenNumber}-${index}`} className="border-t border-gray-100">
              <td className="py-1.5 text-gray-900">{orderLabel(line, language)}</td>
              <td className="py-1.5 text-center text-gray-700">{line.quantity}</td>
              <td className="py-1.5 text-right text-gray-700">
                {(line.unitPrice ?? 0).toFixed(2)}
              </td>
              <td className="py-1.5 text-right text-gray-900">{(line.amount ?? 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-gray-300 mt-2 pt-2 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('Subtotal')}</span>
          <span className="text-gray-900">{money(bill.subtotal)}</span>
        </div>
        {bill.discount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">{t('Discount')}</span>
            <span className="text-gray-900">− {money(bill.discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-1">
          <span>{t('Total')}</span>
          <span>{money(bill.total)}</span>
        </div>
      </div>

      <div className="text-center mt-4 pt-3 border-t border-gray-300">
        <p className="text-sm font-semibold text-gray-900">
          {bill.status === 'paid' ? `PAID · ${PAYMENT_LABELS[bill.paymentType] || ''}` : 'UNPAID'}
        </p>
        {/* A due is paid on paper but not in money, so the bill has to say whose
            account it went on */}
        {bill.paymentType === 'due' && bill.dueCustomerName && (
          <p className="text-xs text-gray-600 mt-0.5">On account · {bill.dueCustomerName}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">{t('Thank you')}</p>
      </div>
    </div>
  );
}

const PAYMENT_LABELS = {
  cash: 'CASH',
  bank: 'BANK',
  due: 'DUE'
};

export default BillModal;
